import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { asyncTasksListing } from "@/network/network.jsx";
import { ASYNC_TASK_STATUS } from "@/other/constants/OtherKeys.jsx";
import AuthContext from "@/react/context/authContext.jsx";

// ── Types ───────────────────────────────────────────────────────────────────

export type TaskLifecycle = "pending" | "completed" | "failed";
export type TaskEvent = "completed" | "failed" | "started";

export interface TaskEntry {
  id: string;
  status: TaskLifecycle;
}

export interface RawAsyncTask {
  id: number;
  type: number;
  status: string;
  meta_data?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface AsyncTaskHandlerConfig<
  TTaskType extends string,
  TPhase extends string,
> {
  taskTypeMap: Record<number, TTaskType>;
  filterTask: (task: RawAsyncTask) => boolean;
  getGroupKey: (task: RawAsyncTask) => number | null;
  derivePhase: (state: Record<TTaskType, TaskEntry | null>) => TPhase;
  fastPollMs?: number;
  idlePollMs?: number;
}

interface EventCallback<TTaskType extends string> {
  groupKey: number;
  taskType: TTaskType;
  event: TaskEvent;
  cb: (task: { id: string }) => void;
}

export interface AsyncTaskHandlerValue<
  TTaskType extends string,
  TPhase extends string,
> {
  isReady: boolean;
  getTaskState: (groupKey: number) => Record<TTaskType, TaskEntry | null> | null;
  getPhase: (groupKey: number) => TPhase;
  onTaskEvent: (
    groupKey: number,
    taskType: TTaskType,
    event: TaskEvent,
    cb: (task: { id: string }) => void
  ) => () => void;
  reportTaskStarted: (groupKey: number, taskType: TTaskType, taskId: string) => void;
  repoll: () => Promise<void>;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function normalizeLifecycle(status: string): TaskLifecycle {
  if (status === ASYNC_TASK_STATUS.COMPLETED) return "completed";
  if (status === ASYNC_TASK_STATUS.FAILED || status === ASYNC_TASK_STATUS.CANCELLED) return "failed";
  return "pending";
}

// ── Context ─────────────────────────────────────────────────────────────────

const AsyncTaskCtx = createContext<AsyncTaskHandlerValue<any, any> | undefined>(undefined);

const DEFAULT_FAST_POLL_MS = 3_000;
const DEFAULT_IDLE_POLL_MS = 10_000;

interface AsyncTaskProviderProps<TTaskType extends string, TPhase extends string> {
  config: AsyncTaskHandlerConfig<TTaskType, TPhase>;
  children: ReactNode;
}

export function AsyncTaskProvider<TTaskType extends string, TPhase extends string>({
  config,
  children,
}: AsyncTaskProviderProps<TTaskType, TPhase>) {
  const { user } = useContext(AuthContext);
  const isAuthenticated = Boolean(user);
  const isAuthenticatedRef = useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;

  const [isReady, setIsReady] = useState(false);
  const [revision, setRevision] = useState(0);

  const taskTypes = useMemo(
    () => Object.values(config.taskTypeMap) as TTaskType[],
    [config.taskTypeMap]
  );

  const makeEmptyState = useCallback((): Record<TTaskType, TaskEntry | null> => {
    const state = {} as Record<TTaskType, TaskEntry | null>;
    for (const t of taskTypes) state[t] = null;
    return state;
  }, [taskTypes]);

  const taskMapRef = useRef<Map<number, Record<TTaskType, TaskEntry | null>>>(new Map());
  const listenersRef = useRef<Set<EventCallback<TTaskType>>>(new Set());
  const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const pollRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const configRef = useRef(config);
  configRef.current = config;

  const fastPoll = config.fastPollMs ?? DEFAULT_FAST_POLL_MS;
  const idlePoll = config.idlePollMs ?? DEFAULT_IDLE_POLL_MS;

  const emitEvent = useCallback(
    (groupKey: number, taskType: TTaskType, event: TaskEvent, taskId: string) => {
      for (const listener of listenersRef.current) {
        if (
          listener.groupKey === groupKey &&
          listener.taskType === taskType &&
          listener.event === event
        ) {
          listener.cb({ id: taskId });
        }
      }
    },
    []
  );

  const reportTaskStarted = useCallback(
    (groupKey: number, taskType: TTaskType, taskId: string) => {
      if (!isAuthenticatedRef.current) return;

      const map = taskMapRef.current;
      const existing = map.get(groupKey) ?? makeEmptyState();

      existing[taskType] = { id: taskId, status: "pending" };
      map.set(groupKey, { ...existing });
      setRevision((r) => r + 1);
      emitEvent(groupKey, taskType, "started", taskId);

      void pollRef.current();
    },
    [emitEvent, makeEmptyState]
  );

  const poll = useCallback(async () => {
    if (!isAuthenticatedRef.current) return;

    const cfg = configRef.current;

    try {
      const res = await asyncTasksListing();
      if (!mountedRef.current || !isAuthenticatedRef.current) return;

      const allTasks: RawAsyncTask[] = res.data ?? [];
      const relevantTasks = allTasks.filter((t) => cfg.filterTask(t));

      const nextMap = new Map<number, Record<TTaskType, TaskEntry | null>>();

      for (const [key, state] of taskMapRef.current.entries()) {
        nextMap.set(key, { ...state });
      }

      const byGroup = new Map<number, Map<TTaskType, RawAsyncTask>>();
      for (const task of relevantTasks) {
        const groupKey = cfg.getGroupKey(task);
        if (groupKey == null) continue;

        const taskType = cfg.taskTypeMap[task.type];
        if (!taskType) continue;

        if (!byGroup.has(groupKey)) byGroup.set(groupKey, new Map());
        const typeMap = byGroup.get(groupKey)!;

        const existing = typeMap.get(taskType);
        if (!existing || Number(task.id) > Number(existing.id)) {
          typeMap.set(taskType, task);
        }
      }

      for (const [groupKey, typeMap] of byGroup.entries()) {
        const prevState = taskMapRef.current.get(groupKey);
        const state: Record<TTaskType, TaskEntry | null> =
          nextMap.get(groupKey) ?? makeEmptyState();

        for (const [taskType, task] of typeMap.entries()) {
          const lifecycle = normalizeLifecycle(task.status);
          const newEntry: TaskEntry = { id: String(task.id), status: lifecycle };

          const prevEntry = prevState?.[taskType];

          if (prevEntry?.status === "pending" && lifecycle !== "pending") {
            emitEvent(groupKey, taskType, lifecycle as TaskEvent, String(task.id));
          }

          state[taskType] = newEntry;
        }

        nextMap.set(groupKey, state);
      }

      taskMapRef.current = nextMap;
      setRevision((r) => r + 1);

      if (!isReady) setIsReady(true);
    } catch {
      if (!isReady && mountedRef.current && isAuthenticatedRef.current) {
        setIsReady(true);
      }
    }
  }, [isReady, emitEvent, makeEmptyState]);

  pollRef.current = poll;

  // Stop polling when logged out; provider stays mounted across logout.
  useEffect(() => {
    if (!isAuthenticated) {
      if (intervalRef.current != null) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
      taskMapRef.current.clear();
      setIsReady(false);
      setRevision((r) => r + 1);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticatedRef.current) return;

    mountedRef.current = true;

    const scheduleNext = () => {
      if (!isAuthenticatedRef.current) return;

      const hasActive = Array.from(taskMapRef.current.values()).some((state) =>
        Object.values(state).some(
          (entry) => entry != null && (entry as TaskEntry).status === "pending"
        )
      );
      const interval = hasActive ? fastPoll : idlePoll;

      if (intervalRef.current != null) clearTimeout(intervalRef.current);
      intervalRef.current = setTimeout(async () => {
        await pollRef.current();
        if (mountedRef.current && isAuthenticatedRef.current) scheduleNext();
      }, interval);
    };

    void poll().then(() => {
      if (mountedRef.current && isAuthenticatedRef.current) scheduleNext();
    });

    return () => {
      mountedRef.current = false;
      if (intervalRef.current != null) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [poll, fastPoll, idlePoll, isAuthenticated]);

  const getTaskState = useCallback(
    (groupKey: number): Record<TTaskType, TaskEntry | null> | null => {
      void revision;
      return taskMapRef.current.get(groupKey) ?? null;
    },
    [revision]
  );

  const getPhase = useCallback(
    (groupKey: number): TPhase => {
      void revision;
      const state = taskMapRef.current.get(groupKey);
      return configRef.current.derivePhase(state ?? makeEmptyState());
    },
    [revision, makeEmptyState]
  );

  const onTaskEvent = useCallback(
    (
      groupKey: number,
      taskType: TTaskType,
      event: TaskEvent,
      cb: (task: { id: string }) => void
    ): (() => void) => {
      const entry: EventCallback<TTaskType> = { groupKey, taskType, event, cb };
      listenersRef.current.add(entry);
      return () => {
        listenersRef.current.delete(entry);
      };
    },
    []
  );

  const repoll = useCallback(async () => {
    await pollRef.current();
  }, []);

  const value: AsyncTaskHandlerValue<TTaskType, TPhase> = useMemo(
    () => ({ isReady, getTaskState, getPhase, onTaskEvent, reportTaskStarted, repoll }),
    [isReady, getTaskState, getPhase, onTaskEvent, reportTaskStarted, repoll]
  );

  return (
    <AsyncTaskCtx.Provider value={value}>
      {children}
    </AsyncTaskCtx.Provider>
  );
}

export function useAsyncTasks<
  TTaskType extends string,
  TPhase extends string,
>(): AsyncTaskHandlerValue<TTaskType, TPhase> {
  const ctx = useContext(AsyncTaskCtx);
  if (!ctx) {
    throw new Error("useAsyncTasks must be used within an AsyncTaskProvider");
  }
  return ctx as AsyncTaskHandlerValue<TTaskType, TPhase>;
}
